import * as React from 'react';
import { Model, Question } from 'survey-core';
import { SurveyQuestionElementBase } from 'survey-react-ui';
import { Button, styled } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CompaniesContainer } from '../../containers/CompaniesContainer';
import { ICompany, SearchProvider } from '../../types/companyDetails';
import { setShowDetails } from '../../helpers/storage';
import { RefObject } from 'react';
import i18next from 'i18next';

export const COMPANY_STATE_KEY = 'signup:company';
export const MANUAL_ENTERING_STATE_KEY = 'signup:manualEntering';

export const TYPE = 'mui-company-search';

export class MuiCompanySearchModel extends Question {
  getType(): string {
    return TYPE;
  }

  get providers() {
    return this.getPropertyValue('providers');
  }
  set providers(val) {
    this.setPropertyValue('providers', val);
  }

  get customDisabled() {
    return this.getPropertyValue('customDisabled');
  }
  set customDisabled(val) {
    this.setPropertyValue('customDisabled', val);
  }
}

const StyledContainer = styled('div')(({ theme }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse'
  }
}));

const StyledResetContainer = styled('div')(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    position: 'absolute',
    right: -8,
    transform: 'translateX(100%)'
  },
  [theme.breakpoints.down('md')]: {
    marginBottom: theme.spacing(2.5),
    marginLeft: 'auto',
    marginRight: theme.spacing(1.25)
  }
}));

const StyledButton = styled(Button)(() => ({
  textTransform: 'unset',
  fontWeight: 600,
  fontSize: '0.875rem',
  lineHeight: '1.25rem',
  letterSpacing: '0.00625em'
}));

export class MuiCompanySearch extends SurveyQuestionElementBase {
  inputRef = React.createRef<HTMLDivElement>();
  constructor(props: any) {
    super(props);
    this.restoreVariables();
    this.handleChange = this.handleChange.bind(this);
    this.handleBlur = this.handleBlur.bind(this);
    this.handleResetForm = this.handleResetForm.bind(this);
    this.handleManualSelected = this.handleManualSelected.bind(this);
    this.handleClose = this.handleClose.bind(this);
  }

  componentDidMount() {
    super.componentDidMount();
    this.question.questionRef = this.inputRef;
  }

  get question() {
    return this.questionBase;
  }

  get providers() {
    return this.question.providers;
  }

  get customDisabled() {
    return this.question.customDisabled;
  }

  restoreVariables() {
    const model = this.question.survey as Model;

    try {
      model.setVariable('manualEntering', sessionStorage.getItem(MANUAL_ENTERING_STATE_KEY) === 'true');
      model.setVariable('company', JSON.parse(sessionStorage.getItem(COMPANY_STATE_KEY) || ''));
    } catch {}
  }

  handleChange(value: string, company?: ICompany) {
    const model = this.question.survey as Model;
    const savedCompany = model.getVariable('company');

    if (savedCompany && savedCompany.companyName !== value) {
      model.runExpression('resetCompanyDetails()');
    }

    model.setVariable('company', company);
    if (this.question.value !== value) {
      this.question.value = value;
    } else {
      // When exact company name was used to perform search value remains the same
      // Make question to rerender since company variable and other are set
      this.question.setPropertyValue('forceUpdated', Date.now());
    }

    if (company) {
      model.setVariable('manualEntering', false);
      sessionStorage.setItem(MANUAL_ENTERING_STATE_KEY, 'false');

      model.setVariable('showDetails', true);
      setShowDetails();
      sessionStorage.setItem(COMPANY_STATE_KEY, JSON.stringify(company));

      const countryToProviderMap = {
        [SearchProvider.COMPANY_HOUSE]: 'GB',
        [SearchProvider.AUSTRALIAN_BUSINESS_REGISTRY]: 'AU'
      };
      const { packageType } = model.data;
      const { provider } = this.providers[packageType];

      model.setValue('country', countryToProviderMap[provider]);
      model.setValue('townOrCity', company.city);
      model.setValue('address1', company.addressLine1);
      model.setValue('address2', company.addressLine2);
      model.setValue('postalCode', company.postalCode);
    }
  }

  handleBlur({ target: { value } }: React.FocusEvent<HTMLInputElement>) {
    this.question.value = value ? value.replace(/\s+/g, ' ').trim() : value;
  }

  handleResetForm() {
    const model = this.question.survey as Model;
    model.runExpression('resetCompanyDetails()');
  }

  handleManualSelected() {
    const model = this.question.survey as Model;

    model.setVariable('manualEntering', true);
    sessionStorage.setItem(MANUAL_ENTERING_STATE_KEY, 'true');
    this.forceUpdate();
  }

  handleClose() {
    const model = this.question.survey as Model;
    const company = model.getVariable('company');
    const manualEntering = model.getVariable('manualEntering');
    const value = this.question.value;

    if (value && !company && !manualEntering) {
      this.handleManualSelected();
    }
  }

  protected renderElement(): JSX.Element | null {
    const { value = '', title, isRequired, errors, readOnly } = this.question;
    const model = this.question.survey as Model;
    const company = model.getVariable('company');
    const manualEntering = model.getVariable('manualEntering');
    const { packageType } = model.data;
    const { provider, hasDetails } = this.providers[packageType];
    const customDisabled = this.customDisabled && model.runExpression(this.customDisabled);

    return (
      <StyledContainer>
        <CompaniesContainer
          ref={this.inputRef as RefObject<any>}
          item={company}
          title={title}
          isRequired={isRequired}
          value={value}
          onChange={this.handleChange}
          onBlur={this.handleBlur}
          onManualSelected={this.handleManualSelected}
          error={errors.length > 0}
          helperText={errors[0] ? errors[0].locText.text : ''}
          onClose={this.handleClose}
          provider={provider as SearchProvider}
          hasDetails={hasDetails}
          disabled={readOnly || customDisabled}
        />
        <StyledResetContainer>
          <StyledButton
            tabIndex={1}
            color="primary"
            size="small"
            startIcon={<CloseIcon />}
            disabled={!company && !manualEntering}
            onClick={this.handleResetForm}
          >
            {i18next.t('signup:common.resetForm', 'Reset form')}
          </StyledButton>
        </StyledResetContainer>
      </StyledContainer>
    );
  }
}
